import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtDateTime } from "@/components/common";
import DataTable from "@/components/DataTable";
import AdvancedFilters from "@/components/AdvancedFilters";
import QuickFilters from "@/components/QuickFilters";
import { usePaged, useDebounced } from "@/lib/paged";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const QF = [{ key: "type", label: "Types" }, { key: "owner_name", label: "Owners" }, { key: "status", label: "Statuses" }, { key: "course", label: "Courses", get: (r) => (r.course || "").split(", ") }, { key: "campaigns", label: "Campaigns" }, { key: "colleges", label: "Colleges" }, { key: "last_disposition", label: "Last Dispositions" }];
import { Button } from "@/components/ui/button";
import { CalendarClock, AlertTriangle, CalendarDays, Ban, Check } from "lucide-react";
import { toast } from "sonner";

export default function Followups() {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState(() => sessionStorage.getItem("fu:bucket") || "due_today");
  const [buckets, setBuckets] = useState({});
  const [search, setSearch] = useState(() => sessionStorage.getItem("fu:search") || "");
  useEffect(() => { sessionStorage.setItem("fu:bucket", bucket); sessionStorage.setItem("fu:search", search); }, [bucket, search]);
  const dq = useDebounced(search);
  const { rows, reload: load, complete: allLoaded } = usePaged("/followups", { bucket: bucket === "no_followup" ? "" : bucket, search: dq.trim() });
  const [display, setDisplay] = useState(null);
  const [quick, setQuick] = useState(null);
  const FU_FIELDS = useMemo(() => [
    { key: "student_name", label: "Lead Name", type: "text" },
    { key: "type", label: "Type", type: "select", options: ["Call", "WhatsApp", "Email", "Meeting", "Campus Visit", "Video Call"] },
    { key: "owner_name", label: "Owner", type: "text" },
    { key: "status", label: "Status", type: "select", options: ["Pending", "Completed", "Missed"] },
    { key: "notes", label: "Notes", type: "text" },
    { key: "due_at", label: "Due Date", type: "date" },
    { key: "created_at", label: "Created On", type: "date" },
  ], []);

  const loadBuckets = () => api.get("/followups/buckets").then(({ data }) => setBuckets(data));
  useEffect(() => { loadBuckets(); }, []);

  const complete = async (id) => { await api.patch(`/followups/${id}`, { status: "Completed" }); toast.success("Completed"); load(); loadBuckets(); };
  const snooze = async (id) => { await api.patch(`/followups/${id}`, { due_at: new Date(Date.now() + 86400000).toISOString() }); toast.success("Snoozed to tomorrow"); load(); };

  const TABS = [
    { key: "due_today", label: "Due Today", icon: CalendarClock, n: buckets.due_today },
    { key: "overdue", label: "Overdue", icon: AlertTriangle, n: buckets.overdue },
    { key: "upcoming", label: "Upcoming", icon: CalendarDays, n: buckets.upcoming },
    { key: "no_followup", label: "No Follow-up", icon: Ban, n: buckets.no_followup },
  ];

  const columns = [
    { key: "student_name", label: "Lead Name", fixed: true, render: (r) => <span className="font-semibold text-slate-800" data-testid={`fu-name-${r.id}`}>{r.student_name || "—"}</span> },
    { key: "type", label: "Type", render: (r) => <StatusBadge value={r.type} /> },
    { key: "notes", label: "Notes", render: (r) => <span className="text-slate-600">{r.notes || "—"}</span> },
    { key: "owner_name", label: "Owner" },
    { key: "due_at", label: "Due", render: (r) => <span className="text-xs">{fmtDateTime(r.due_at)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "created_at", label: "Created On", hidden: true, render: (r) => <span className="text-xs">{fmtDateTime(r.created_at)}</span> },
    { key: "completed_at", label: "Completed On", hidden: true, render: (r) => <span className="text-xs">{r.completed_at ? fmtDateTime(r.completed_at) : "—"}</span> },
    { key: "actions", label: "", fixed: true, render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => complete(r.id)} data-testid={`fu-complete-${r.id}`}><Check className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => snooze(r.id)}>Snooze</Button>
      </div>) },
  ];

  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="No active lead should be without a next action" testid="followups-header" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon; const active = bucket === t.key;
          return (
            <button key={t.key} onClick={() => setBucket(t.key)} data-testid={`bucket-${t.key}`}
              className={`text-left p-4 rounded-xl border transition-all ${active ? "bg-[#312E81] text-white border-[#312E81]" : "bg-white border-slate-200 hover:border-[#4F46E5]/40"}`}>
              <div className="flex items-center justify-between">
                <Icon className={`w-5 h-5 ${active ? "text-[#4F46E5]" : "text-slate-400"}`} />
                <span className={`font-heading text-2xl font-extrabold ${active ? "text-white" : "text-[#312E81]"}`}>{t.n ?? 0}</span>
              </div>
              <div className={`text-xs mt-1 ${active ? "text-slate-200" : "text-slate-500"}`}>{t.label}</div>
            </button>
          );
        })}
      </div>
      {bucket === "no_followup" ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
          <b>{buckets.no_followup ?? 0}</b> active leads have no scheduled next action. Open Leads to schedule follow-ups.
          <Button size="sm" className="ml-3 bg-[#4F46E5] hover:bg-[#4338CA]" onClick={() => navigate("/leads")}>Go to Leads</Button>
        </div>
      ) : (
        <>
          <div className="relative max-w-xs mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search all follow-ups: lead / mobile / notes" className="pl-9 bg-white" data-testid="followups-search" />
          </div>
          <QuickFilters rows={rows} fields={QF} onFiltered={setQuick} testid="followups" storageKey="followups" />
          <AdvancedFilters entity="followups" fields={FU_FIELDS} rows={quick} onFiltered={setDisplay} persist />
          <DataTable columns={columns} rows={display} loading={rows === null} testid="followups-table" columnsKey="followups" paginate loadingMore={!allLoaded} onRowClick={(r) => r.student_id && navigate(`/students/${r.student_id}`)} empty="Nothing in this bucket" />
        </>
      )}
    </div>
  );
}
